// [AI TEST] This comment was added to test if the edit tool can make changes.
// Window image utilities for email embedding
// This module helps map window types to appropriate images
import { getStyleById } from '../config/windowStyles';

// Define proper type for the base64 data
interface WindowImageBase64Map {
  [key: string]: string;
}

// Define the base path for window images
const BASE_IMAGE_PATH = 'images/windows';

// Default image to use if no match is found
export const DEFAULT_IMAGE = `${BASE_IMAGE_PATH}/Casements/Traditional/Traditional Style One.jpg`;

// Helper function to get image path based on window type and style
export function getImagePathForStyle(windowType: string, styleName: string): string {
  // Normalize type for matching
  const type = windowType.toLowerCase();

  // Map window types to their directory names
  const typeToDir: { [key: string]: string } = {
    'casement': 'Casements',
    'flush-casement': 'Casements',
    'aluminium': 'Casements',
    'tilt-and-turn': 'TiltAndTurn',
    'sliding-sash': 'Sliding Sash'
  };

  const dir = typeToDir[type] || 'Casements';
  
  // Check if styleName is a numeric style pattern (e.g., "Style 1")
  const styleNumMatch = styleName?.match(/^Style\s+(\d+)$/i);
  if (styleNumMatch) {
    const styleNum = parseInt(styleNumMatch[1]);
    // Map numeric styles (Style 1, Style 2) to Traditional Style One, Two, Three, Four
    const styleWords = ['One', 'Two', 'Three', 'Four', 'Five'];
    const styleWord = styleNum <= styleWords.length ? styleWords[styleNum - 1] : 'One';
    
    // Return the Traditional style path
    return `${BASE_IMAGE_PATH}/${dir}/Traditional/Traditional Style ${styleWord}.jpg`;
  }
  
  return `${BASE_IMAGE_PATH}/${dir}/${styleName}.jpg`;
}

// Synchronous version for UI components
export function getWindowImageUrlSync(windowType: string, styleName: string): string {
  try {
    // Always use relative path which will resolve from the public folder
    return getImagePathForStyle(windowType, styleName);
  } catch (error) {
    console.error("Error generating window image URL:", error);
    return DEFAULT_IMAGE;
  }
}

// Utility to load all base64 chunks using the manifest
export async function loadAllWindowImageBase64Chunks(): Promise<WindowImageBase64Map> {
  try {
    // Browser-only implementation
    try {
      // First try to import the combined JSON file
      const data = await import('../data/windowImageBase64.json');
      console.log("Successfully loaded windowImageBase64.json");
      return data.default || data;
    } catch (importError) {
      console.warn("Could not load windowImageBase64.json, will use empty data:", importError);
      return {};
    }
  } catch (error) {
    console.error('Error in loadAllWindowImageBase64Chunks:', error);
    return {};
  }
}

// Async version for email/PDF generation
export async function getWindowImageUrl(windowType: string, styleName: string): Promise<string> {
  try {
    // Try to get base64 data first
    try {
      // Load all base64 chunks using the manifest
      const windowImageBase64 = await loadAllWindowImageBase64Chunks();
      
      // Get the image path from the style configuration
      const imagePath = getImagePathForStyle(windowType, styleName);
      
      // Only strip the images/windows/ prefix, preserve case
      const relativePath = imagePath.replace(/^images\/windows\//, '');
      
      // Try exact match only
      if (windowImageBase64[relativePath]) {
        return `data:image/jpeg;base64,${windowImageBase64[relativePath]}`;
      }
      
      // If no match found, log and continue to fallback
      console.log('No base64 data found for:', relativePath);
    } catch (e) {
      console.error('Error loading base64 image data:', e);
    }
    
    // Fallback: use relative path for browser environment
    return getImagePathForStyle(windowType, styleName);
  } catch (error) {
    console.error("Error generating window image URL:", error);
    return '';
  }
} 